<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :to="tag === 'router-link' ? to : undefined"
    :href="tag === 'a' ? href : undefined"
    :disabled="disabled || loading"
    :class="buttonClasses"
    v-bind="$attrs"
    @click="handleClick"
  >
    <!-- Loading Spinner -->
    <svg
      v-if="loading"
      class="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>

    <!-- Icon (left) -->
    <span v-if="$slots.icon && iconPosition === 'left'" class="mr-2">
      <slot name="icon" />
    </span>

    <!-- Button Content -->
    <slot />

    <!-- Icon (right) -->
    <span v-if="$slots.icon && iconPosition === 'right'" class="ml-2">
      <slot name="icon" />
    </span>
  </component>
</template>

<script>
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base styles - flat design, no transforms, appearance-none removes browser 3D styling
  'inline-flex items-center justify-center font-semibold appearance-none transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        // =============================================
        // CENTRALIZED FLAT BUTTON SYSTEM (2026)
        // All buttons should use these variants
        // No shadows, gradients, or transforms
        // =============================================

        // PRIMARY - Main call-to-action (Add to Cart, Submit, etc.)
        // Uses --primary-action-bg for contrast (dark navy on Windermere vs teal accent)
        primary: 'btn-primary-themed text-white',

        // SECONDARY - Outlined, less emphasis
        secondary: 'bg-transparent text-[--button-color] border-2 border-[--button-color] hover:bg-[--button-color] hover:text-white focus-visible:ring-[--button-color]',

        // OUTLINE - Subtle outline, minimal
        outline: 'bg-transparent text-gray-700 border border-gray-300 hover:border-gray-400 hover:bg-gray-50 focus-visible:ring-gray-400',

        // GHOST - No border, just hover background
        ghost: 'bg-transparent text-gray-700 hover:bg-black/5 focus-visible:ring-gray-400',

        // HEADER - For header actions (cart, login, menu)
        header: 'bg-transparent text-gray-600 border border-transparent hover:bg-black/[0.04] hover:border-black/[0.08] focus-visible:ring-[--button-color]',

        // DANGER - Destructive actions (remove, delete)
        danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',

        // DANGER-GHOST - Subtle destructive (remove from cart)
        dangerGhost: 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 hover:border-red-200 focus-visible:ring-red-500',

        // SUCCESS - Confirmations
        success: 'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',

        // QUANTITY - For +/- stepper buttons
        quantity: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 focus-visible:ring-[--button-color]',

        // QUANTITY-MINUS - Decrease with red hint
        quantityMinus: 'bg-white text-gray-700 border border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 focus-visible:ring-red-500',

        // QUANTITY-PLUS - Increase with green hint
        quantityPlus: 'bg-white text-gray-700 border border-gray-200 hover:bg-green-50 hover:border-green-200 hover:text-green-600 focus-visible:ring-green-500',

        // PILL - Category/filter pills
        pill: 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 hover:border-gray-300 rounded-full focus-visible:ring-[--button-color]',

        // PILL-ACTIVE - Selected pill state
        pillActive: 'bg-[--button-color] text-white border border-[--button-color] hover:brightness-90 rounded-full focus-visible:ring-[--button-color]',

        // LINK - Text-only button that looks like a link
        link: 'bg-transparent text-[--button-color] hover:underline underline-offset-4 focus-visible:ring-[--button-color]',

        // SOFT - Subtle background, low emphasis
        soft: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus-visible:ring-gray-400',

        // =============================================
        // LEGACY ALIASES (map to new variants)
        // =============================================
        solid: 'btn-primary-themed text-white',
        headerAction: 'bg-transparent text-gray-600 border border-transparent hover:bg-black/[0.04] hover:border-black/[0.08] focus-visible:ring-[--button-color]',
        themedSecondary: 'bg-transparent text-[--button-color] border-2 border-[--button-color] hover:bg-[--button-color] hover:text-white focus-visible:ring-[--button-color]',
        themedGhost: 'bg-transparent text-gray-700 hover:bg-black/5 focus-visible:ring-gray-400',
        themedDanger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        themedSuccess: 'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',
        destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        primaryGlow: 'btn-primary-themed text-white',
        accentGlow: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        successGlow: 'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',
        softPrimary: 'bg-primary-50 text-primary-700 hover:bg-primary-100 focus-visible:ring-primary-500',
        glass: 'bg-white/20 backdrop-blur-md text-white border border-white/30 hover:bg-white/30 focus-visible:ring-white/50',
      },
      size: {
        sm: 'px-3 py-1.5 text-sm rounded-lg',
        md: 'px-4 py-2.5 text-sm rounded-lg',
        lg: 'px-6 py-3 text-base rounded-lg',
        icon: 'h-11 w-11 rounded-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: ''
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false
    },
  }
)

export default {
  name: 'Button',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => [
        // CORE VARIANTS (recommended for new code)
        'primary', 'secondary', 'outline', 'ghost', 'header', 'link', 'soft',
        'danger', 'dangerGhost', 'success',
        'quantity', 'quantityMinus', 'quantityPlus',
        'pill', 'pillActive',
        // LEGACY ALIASES (for backwards compatibility)
        'solid', 'headerAction', 'themedSecondary', 'themedGhost', 'themedDanger', 'themedSuccess',
        'destructive', 'primaryGlow', 'accentGlow', 'successGlow', 'softPrimary', 'glass'
      ].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'icon'].includes(value)
    },
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },
    to: {
      type: [String, Object],
      default: null
    },
    href: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    iconPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value)
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    tag() {
      // Auto-determine tag based on props
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return 'button'
    },
    buttonClasses() {
      return cn(
        buttonVariants({
          variant: this.variant,
          size: this.size,
          fullWidth: this.fullWidth
        }),
        this.customClass
      )
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    }
  }
}
</script>

<style scoped>
/* Ensure flat styling across all browsers */
button {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  box-shadow: none;
}

/* Primary themed button - uses CSS variable for theme-specific background
   This approach works reliably with CSS variables unlike Tailwind arbitrary values */
.btn-primary-themed {
  background-color: var(--primary-action-bg, #02233c);
  transition: background-color 0.2s ease;
}

.btn-primary-themed:hover:not(:disabled) {
  background-color: var(--primary-action-bg-hover, #011a2c);
}

.btn-primary-themed:focus-visible {
  --tw-ring-color: var(--primary-action-bg, #02233c);
}

.btn-primary-themed:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
